import { getToken } from '@helpscout/hsds-tokens/getToken'
import styled from 'styled-components'

import {
  BASE_FONT_SIZE,
  SMALL_TEXT_FONT_VARIANT_ALTERNATES,
  variableFontSize,
} from 'hsds/utils/fonts'
import { generateLinkStyles } from 'hsds/utils/mixins'

export const VAR_NAMESPACE_SIZE = 'hsds-global-heading-font-size'

export const HEADING_SIZES = {
  h1: 32,
  h2: 24,
  h3: 20,
  h4: 16,
  h5: 14,
  h6: BASE_FONT_SIZE,
  xl: 22,
  big: 20,
  md: 18,
  sm: 15,
  small: 11,
}

// Headings at or above this size keep Scoutie's looptail "g"; smaller ones use
// the single-storey "g" (ss07) like the rest of the UI text.
const LOOPTAIL_G_MIN_SIZE = 18

const smallHeadingSelectors = Object.entries(HEADING_SIZES)
  .filter(([, size]) => size < LOOPTAIL_G_MIN_SIZE)
  .map(([name]) => `&.is-${name}`)
  .join(', ')

export const HeadingUI = styled.div`
  color: ${getToken('heading.color.text')};
  display: block;
  font-size: ${HEADING_SIZES.h1}px;
  font-weight: ${getToken('font.weight.medium')};
  font-variant-alternates: normal;
  line-height: 1.2;

  ${smallHeadingSelectors} {
    font-variant-alternates: ${SMALL_TEXT_FONT_VARIANT_ALTERNATES};
  }

  &.is-light {
    color: ${getToken('heading.color.light')};
  }

  &.is-h1 {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['h1'],
    })}
  }

  &.is-h2 {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['h2'],
    })}
  }

  &.is-h3 {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['h3'],
    })}
  }

  &.is-h4 {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['h4'],
    })}
  }

  &.is-h5 {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['h5'],
    })}
  }

  &.is-h6 {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['h6'],
    })}
  }

  &.is-xl {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['xl'],
    })}
  }

  &.is-big {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['big'],
    })}
    font-weight: ${getToken('font.weight.bold')};
  }

  &.is-md {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['md'],
    })}
  }

  &.is-sm {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['sm'],
    })}
    line-height: 1.6;
  }

  &.is-small {
    ${variableFontSize({
      varName: VAR_NAMESPACE_SIZE,
      fontSize: HEADING_SIZES['small'],
    })}
    color: ${getToken('heading.color.small.text')};
    letter-spacing: 0.5px;
    text-transform: uppercase;

    &.is-light {
      color: ${getToken('heading.color.light')};
    }
  }

  &.is-disableSelect {
    user-select: none;
  }

  &.is-center {
    text-align: center;
  }

  &.is-linkStyle {
    ${generateLinkStyles()}
  }

  &.is-truncate {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.is-lineHeightInherit {
    line-height: inherit;
  }

  &.is-lineHeightReset {
    line-height: 1;
  }

  &.is-wordWrap {
    word-break: break-word;
  }

  &.is-noWrap {
    white-space: nowrap;
  }

  &.is-100 {
    font-weight: 100;
  }

  &.is-200 {
    font-weight: 200;
  }

  &.is-300 {
    font-weight: ${getToken('font.weight.light')};
  }

  &.is-400 {
    font-weight: ${getToken('font.weight.regular')};
  }

  &.is-500 {
    font-weight: ${getToken('font.weight.medium')};
  }

  &.is-600 {
    font-weight: ${getToken('font.weight.bold')};
  }

  &.is-700 {
    font-weight: ${getToken('font.weight.bold')};
  }

  &.is-800 {
    font-weight: 800;
  }

  &.is-900 {
    font-weight: 900;
  }
`
